import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { RECIPIENT_TYPES } from 'components/common/constants';
import { CYRILLIC_REGEXP } from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import ValidateRecipientDetailsModel from 'components/Templates/models/ValidateRecipientDetailsModel';
import i18nContext from 'components/i18n-context';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import Button from 'uikit/Button/Button';
import './RecipientDetailsStep.scss';
import { getTemplateStepErrorMessage } from 'components/Templates/utils/getTemplateStepErrorMessage';

const RecipientDetailsStep = ({ paymentStore, customerNumber }) => {
  const i18n = useContext(i18nContext);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      recipientType: paymentStore.templateData?.recipientType || RECIPIENT_TYPES.COMPANY,
      companyName: paymentStore.templateData?.companyName || '',
      firstName: paymentStore.templateData?.firstName || '',
      lastName: paymentStore.templateData?.lastName || ''
    },
    validationSchema: Yup.object().shape({
      companyName: Yup.string().when('recipientType', {
        is: (recipientType) => recipientType === RECIPIENT_TYPES.COMPANY,
        then: (schema) =>
          schema
            .trim()
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
            .test(
              'no-cyrillic',
              i18n.getMessage('client.validation.message.noCyrillic'),
              (value) => !CYRILLIC_REGEXP.test(value || '')
            ),
        otherwise: (schema) => schema
      }),
      firstName: Yup.string().when('recipientType', {
        is: (recipientType) => recipientType === RECIPIENT_TYPES.INDIVIDUAL,
        then: (schema) =>
          schema
            .trim()
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(125, i18n.getMessage('client.validation.message.maxSize', { size: 125 }))
            .test(
              'no-cyrillic',
              i18n.getMessage('client.validation.message.noCyrillic'),
              (value) => !CYRILLIC_REGEXP.test(value || '')
            ),
        otherwise: (schema) => schema
      }),
      lastName: Yup.string().when('recipientType', {
        is: (recipientType) => recipientType === RECIPIENT_TYPES.INDIVIDUAL,
        then: (schema) =>
          schema
            .trim()
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(125, i18n.getMessage('client.validation.message.maxSize', { size: 125 }))
            .test(
              'no-cyrillic',
              i18n.getMessage('client.validation.message.noCyrillic'),
              (value) => !CYRILLIC_REGEXP.test(value || '')
            ),
        otherwise: (schema) => schema
      })
    }),
    onSubmit: async (values) => {
      const requestData = ValidateRecipientDetailsModel({ ...paymentStore.templateData, ...values });

      paymentStore.setTemplateData({ ...paymentStore.templateData, ...values });

      await paymentStore.validateTemplateRecipientDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange, setFieldValue } = form;

  const handleRecipientTypeChange = (name, value) => {
    setFieldValue('recipientType', value);

    if (value === RECIPIENT_TYPES.COMPANY) {
      setFieldValue('firstName', '');
      setFieldValue('lastName', '');
    } else {
      setFieldValue('companyName', '');
    }
  };

  const serverError = paymentStore.error;

  return (
    <form onSubmit={handleSubmit} className={'recipient-details-step'}>
      <div className={'recipient-details-step-inputs-wrapper'}>
        <InputDropDown
          className={'recipient-details-step-input'}
          label={i18n.getMessage('templates.form.recipientType.label')}
          name={'recipientType'}
          value={form.values.recipientType}
          onChange={handleRecipientTypeChange}
          options={Object.values(RECIPIENT_TYPES).map((value) => ({ key: value, value }))}
          isRequired
          error={errors.recipientType || getTemplateStepErrorMessage(i18n, serverError, 'recipientType')}
        />

        {values.recipientType === RECIPIENT_TYPES.COMPANY ? (
          <Input
            className={'recipient-details-step-input'}
            name={'companyName'}
            label={i18n.getMessage('templates.form.companyName.label')}
            placeholder={i18n.getMessage('templates.form.companyName.placeholder')}
            value={values.companyName}
            error={errors.companyName || getTemplateStepErrorMessage(i18n, serverError, 'companyName')}
            onChange={handleChange}
            isRequired
          />
        ) : (
          <>
            <Input
              className={'recipient-details-step-input'}
              name={'firstName'}
              label={i18n.getMessage('templates.form.firstName.label')}
              placeholder={i18n.getMessage('templates.form.firstName.placeholder')}
              value={values.firstName}
              error={errors.firstName || getTemplateStepErrorMessage(i18n, serverError, 'firstName')}
              onChange={handleChange}
              isRequired
            />
            <Input
              className={'recipient-details-step-input'}
              name={'lastName'}
              label={i18n.getMessage('templates.form.lastName.label')}
              placeholder={i18n.getMessage('templates.form.lastName.placeholder')}
              value={values.lastName}
              error={errors.lastName || getTemplateStepErrorMessage(i18n, serverError, 'lastName')}
              onChange={handleChange}
              isRequired
            />
          </>
        )}
      </div>

      <Button
        className={'recipient-details-step-next-button'}
        type={'primary'}
        roleType={'submit'}
        onClick={handleSubmit}
        isDisabled={paymentStore.isLoading}
      >
        {i18n.getMessage('templates.form.button.next')}
      </Button>
    </form>
  );
};

RecipientDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore
}))(observer(RecipientDetailsStep));
